/**
 * Tek merkezden hızlı görüntü kopyalama.
 * - html2canvas yok
 * - element veya inline HTML → PNG → pano
 * Bu dosyayı düzeltince tüm modaller düzelir.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { elementToPngBlob, htmlToPngBlob, writePngToClipboard } from '../utils/fastPngCapture';

const playCameraShutterSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const now = audioContext.currentTime;
    const click1 = audioContext.createOscillator();
    const gain1 = audioContext.createGain();
    click1.frequency.value = 800;
    click1.type = 'square';
    click1.connect(gain1);
    gain1.connect(audioContext.destination);
    gain1.gain.setValueAtTime(0, now);
    gain1.gain.linearRampToValueAtTime(0.6, now + 0.003);
    gain1.gain.exponentialRampToValueAtTime(0.01, now + 0.15);
    click1.start(now);
    click1.stop(now + 0.15);
  } catch (_) {
    /* ignore */
  }
};

const playCopyFlash = () => {
  playCameraShutterSound();
  const flashOverlay = document.createElement('div');
  flashOverlay.style.cssText = `
    position: fixed; top: 0; left: 0; width: 100%; height: 100%;
    background: rgba(255,255,255,0.9); z-index: 99999; pointer-events: none;
    opacity: 0; transition: opacity 0.1s ease-out;
  `;
  document.body.appendChild(flashOverlay);
  requestAnimationFrame(() => {
    flashOverlay.style.opacity = '1';
    setTimeout(() => {
      flashOverlay.style.opacity = '0';
      setTimeout(() => {
        if (document.body.contains(flashOverlay)) document.body.removeChild(flashOverlay);
      }, 100);
    }, 80);
  });
};

/** Tek seferlik: elementi yakala ve panoya yaz */
export const copyElementAsPng = async (element, options = {}) => {
  const {
    successMessage = 'Panoya kopyalandı',
    width
  } = options;
  const blob = await elementToPngBlob(element, { width });
  if (!blob) throw new Error('Görüntü oluşturulamadı');
  await writePngToClipboard(blob);
  toast.success(successMessage);
  playCopyFlash();
  return blob;
};

/**
 * Modal / alan için hazırlık + kopya / WhatsApp / yazdır.
 *
 * @param {object} opts
 * @param {boolean} opts.isOpen
 * @param {boolean} opts.ready - veri yüklendi mi
 * @param {React.RefObject} [opts.targetRef] - yakalanacak DOM
 * @param {() => string} [opts.buildHtml] - dekont gibi SVG HTML üretici
 * @param {number} [opts.htmlWidth]
 * @param {string} [opts.fileName]
 * @param {string} [opts.successMessage]
 * @param {string} [opts.printTitle]
 * @param {string} [opts.shareTitle]
 * @param {string} [opts.shareText]
 */
export const useFastImageCopy = ({
  isOpen = true,
  ready = true,
  targetRef = null,
  buildHtml = null,
  htmlWidth = 920,
  fileName = 'goruntu.png',
  successMessage = 'Panoya kopyalandı',
  printTitle = 'Yazdır',
  shareTitle = 'Paylaş',
  shareText = ''
} = {}) => {
  const [preparing, setPreparing] = useState(false);
  const preparedBlobRef = useRef(null);
  const prepareGenRef = useRef(0);
  const buildHtmlRef = useRef(buildHtml);
  buildHtmlRef.current = buildHtml;

  const captureBlob = useCallback(async () => {
    if (typeof buildHtmlRef.current === 'function') {
      const html = buildHtmlRef.current();
      if (!html) return null;
      return htmlToPngBlob(html, htmlWidth);
    }
    const el = targetRef?.current;
    if (!el) return null;
    return elementToPngBlob(el);
  }, [targetRef, htmlWidth]);

  // Açık + hazır olunca arka planda hazırla
  useEffect(() => {
    if (!isOpen) {
      prepareGenRef.current += 1;
      preparedBlobRef.current = null;
      setPreparing(false);
      return undefined;
    }
    if (!ready) {
      preparedBlobRef.current = null;
      setPreparing(true);
      return undefined;
    }

    const gen = ++prepareGenRef.current;
    preparedBlobRef.current = null;
    setPreparing(true);

    let cancelled = false;
    (async () => {
      try {
        await new Promise((r) => requestAnimationFrame(r));
        if (cancelled || gen !== prepareGenRef.current) return;
        if (!buildHtmlRef.current && !targetRef?.current) return;
        const blob = await captureBlob();
        if (cancelled || gen !== prepareGenRef.current) return;
        if (!blob || blob.size < 800) throw new Error('Görüntü oluşturulamadı');
        preparedBlobRef.current = blob;
      } catch (error) {
        if (!cancelled && gen === prepareGenRef.current) {
          preparedBlobRef.current = null;
          console.error('Kopya hazırlık hatası:', error);
        }
      } finally {
        if (!cancelled && gen === prepareGenRef.current) setPreparing(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, ready, captureBlob, targetRef]);

  const ensureBlob = useCallback(async () => {
    if (preparedBlobRef.current) return preparedBlobRef.current;
    const blob = await captureBlob();
    if (blob) preparedBlobRef.current = blob;
    return blob;
  }, [captureBlob]);

  const copyImage = useCallback(async () => {
    setPreparing(true);
    try {
      const blob = await ensureBlob();
      if (!blob) {
        toast.error('Kopyalama başarısız');
        return false;
      }
      await writePngToClipboard(blob);
      toast.success(successMessage);
      playCopyFlash();
      return true;
    } catch (error) {
      console.error('Kopyalama hatası:', error);
      toast.error(error?.message || 'Kopyalama başarısız');
      return false;
    } finally {
      setPreparing(false);
    }
  }, [ensureBlob, successMessage]);

  const shareWhatsApp = useCallback(async () => {
    setPreparing(true);
    try {
      const blob = await ensureBlob();
      if (!blob) {
        toast.error('Resim oluşturulamadı');
        return false;
      }
      const file = new File([blob], fileName, { type: 'image/png' });
      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: shareTitle, text: shareText });
        toast.success('WhatsApp\'ta paylaşıldı');
      } else {
        window.open('https://web.whatsapp.com/', '_blank');
        toast.success('WhatsApp Web açıldı');
      }
      return true;
    } catch (error) {
      console.error('WhatsApp paylaşım hatası:', error);
      toast.error('WhatsApp paylaşımı başarısız');
      return false;
    } finally {
      setPreparing(false);
    }
  }, [ensureBlob, fileName, shareTitle, shareText]);

  const printImage = useCallback(async () => {
    setPreparing(true);
    try {
      const blob = await ensureBlob();
      if (!blob) {
        toast.error('Yazdırma hazırlığı başarısız');
        return false;
      }
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      const w = window.open('', '_blank');
      if (!w) {
        toast.error('Popup engellendi');
        return false;
      }
      w.document.write(`<!doctype html><html><head><title>${printTitle}</title>
        <style>@page{size:A5 landscape;margin:0}body{margin:0}img{width:100%;display:block}</style>
        </head><body><img src="${dataUrl}" /></body></html>`);
      w.document.close();
      w.onload = () => { w.focus(); w.print(); };
      return true;
    } catch (error) {
      console.error('Yazdırma hazırlık hatası:', error);
      toast.error('Yazdırma hazırlığı başarısız');
      return false;
    } finally {
      setPreparing(false);
    }
  }, [ensureBlob, printTitle]);

  return {
    preparing,
    copyImage,
    shareWhatsApp,
    printImage,
    ensureBlob
  };
};

export default useFastImageCopy;
